// composables/useLocalStorage.ts
export const useLocalStorage = () => {
  /**
   * Stores a value in the browser's sessionStorage under the specified key.
   * If the value is an object, it is stringified to JSON before storing.
   * This function only executes on the client side.
   *
   * @param key - The key under which the value will be stored.
   * @param value - The value to store; can be a string or an object.
   */
  const setItem = (key: string, value: string | object): void => {
    if (import.meta.client) {
      sessionStorage.setItem(key, typeof value === "object" ? JSON.stringify(value) : value);
    }
  };

  /**
   * Retrieves an item from sessionStorage or localStorage by the given key.
   *
   * - If the item is not found in sessionStorage, it attempts to retrieve it from localStorage.
   * - If found in localStorage, the item is moved to sessionStorage and removed from localStorage.
   * - Attempts to parse the item as JSON; if parsing fails, returns the raw string.
   * - Returns `null` if the item is not found or if not running in a client environment.
   *
   * @param key - The key of the item to retrieve.
   * @returns The parsed object, string value, or `null` if not found.
   */
  const getItem = (key: string): string | object | null => {
    if (import.meta.client) {
      let item = sessionStorage.getItem(key);

      if (!item) {
        item = localStorage.getItem(key);

        if (item) {
          sessionStorage.setItem(key, item);
          localStorage.removeItem(key);
        }
      }

      if (!item) return null;
      try {
        return JSON.parse(item);
      } catch {
        return item;
      }
    }
    return null;
  };

  /**
   * Removes an item from the session storage by the specified key.
   *
   * This function checks if the code is running on the client side before attempting
   * to remove the item from `sessionStorage`. If not running on the client, it does nothing.
   *
   * @param key - The key of the item to remove from session storage.
   */
  const removeItem = (key: string): void => {
    if (import.meta.client) {
      sessionStorage.removeItem(key);
    }
  };

  return {
    setItem,
    getItem,
    removeItem,
  };
};
